/**
 * AdminAIFiling.js
 * =================
 * Admin page showing all AI filing sessions across all users.
 * Route: /admin/ai-filing
 * Migration: Cloudflare Worker removed — calls now go to FastAPI backend via shared `api` instance.
 */

import { useState, useEffect } from 'react';
import { AdminLayout }          from '../components/AdminLayout';
import { api } from "@/lib/api";
import { toast }                from 'sonner';
import {
  Sparkles, CheckCircle, Clock, AlertCircle, X,
  ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';
import { Badge }   from '@/components/ui/badge';
import { Button }  from '@/components/ui/button';

// All admin AI filing calls go to FastAPI backend via the shared api instance.
const cfApi = api;

const STATUS_CFG = {
  completed:      { label: 'Completed',     color: '#2e7d32', icon: CheckCircle },
  processing:     { label: 'Processing',    color: '#ed6c02', icon: RefreshCw   },
  uploading:      { label: 'Uploading',     color: '#0f2e1f', icon: Clock       },
  needs_reupload: { label: 'Action needed', color: '#f59e0b', icon: AlertCircle },
  failed:         { label: 'Failed',        color: '#d32f2f', icon: X           },
};

export default function AdminAIFiling() {
  const [sessions, setSessions] = useState([]);
  const [stats,    setStats   ] = useState(null);
  const [loading,  setLoading ] = useState(true);
  const [page,     setPage    ] = useState(1);
  const [total,    setTotal   ] = useState(0);
  const [filterFY, setFilterFY] = useState(() => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();
    const fyEnd = month >= 4 ? year : year - 1;
    return `${fyEnd - 1}-${fyEnd}`;
  });
  const [filterStatus, setFilterStatus] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [detail,   setDetail  ] = useState({});

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchSessions(); }, [page, filterFY, filterStatus]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/ai-filing/stats');
      setStats(res.data);
    } catch { }
  };

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, financial_year: filterFY });
      if (filterStatus) params.set('status', filterStatus);
      const res = await api.get(`/admin/ai-filing/sessions?${params}`);
      setSessions(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load sessions');
    } finally { setLoading(false); }
  };

  const toggleExpand = async (sessionId) => {
    if (expanded === sessionId) { setExpanded(null); return; }
    setExpanded(sessionId);
    if (!detail[sessionId]) {
      try {
        const res = await api.get(`/admin/ai-filing/sessions/${sessionId}`);
        setDetail(prev => ({ ...prev, [sessionId]: res.data }));
      } catch { toast.error('Failed to load session detail'); }
    }
  };

  const fmt = n => n != null ? `₹${Math.abs(n).toLocaleString('en-IN')}` : '—';

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center"
               style={{ backgroundColor: 'var(--accent)' }}>
            <Sparkles size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>AI Filing Sessions</h1>
            <p className="text-sm opacity-60">All AI-generated ITR filing sessions</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              { label: 'Total',      value: stats.total ?? 0 },
              { label: 'Completed',  value: stats.completed ?? 0,      color: '#2e7d32' },
              { label: 'Processing', value: stats.processing ?? 0,     color: '#ed6c02' },
              { label: 'Failed',     value: stats.failed ?? 0,         color: '#d32f2f' },
              { label: 'Uploading',  value: stats.uploading ?? 0,      color: '#666' },
              { label: 'Revenue',    value: `₹${(stats.total_revenue || 0).toLocaleString('en-IN')}` },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-xl p-4 text-center"
                   style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
                <p className="text-xs opacity-55">{label}</p>
                <p className="text-xl font-bold mt-0.5"
                   style={{ fontFamily: 'Fraunces, serif', color }}>
                  {value}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <select value={filterFY} onChange={e => { setFilterFY(e.target.value); setPage(1); }}
                  className="border rounded-lg px-3 py-2 text-sm"
                  style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}>
            {(() => {
              const now = new Date();
              const month = now.getMonth() + 1;
              const year = now.getFullYear();
              const fyEnd = month >= 4 ? year : year - 1;
              return [
                `${fyEnd - 1}-${fyEnd}`,
                `${fyEnd - 2}-${fyEnd - 1}`,
                `${fyEnd - 3}-${fyEnd - 2}`,
              ];
            })().map(fy => (
              <option key={fy} value={fy}>{fy}</option>
            ))}
          </select>
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                  className="border rounded-lg px-3 py-2 text-sm"
                  style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}>
            <option value="">All statuses</option>
            {Object.entries(STATUS_CFG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <Button size="sm" variant="outline" onClick={fetchSessions}>
            <RefreshCw size={13} className="mr-1" /> Refresh
          </Button>
          <span className="text-sm opacity-50 ml-auto">{total} session{total !== 1 ? 's' : ''}</span>
        </div>

        {/* Sessions list */}
        {loading ? (
          <div className="text-center py-12 opacity-40">
            <RefreshCw className="w-8 h-8 mx-auto animate-spin mb-2" />
            <p>Loading sessions…</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 opacity-40">No sessions found.</div>
        ) : (
          <div className="space-y-2">
            {sessions.map(s => {
              const cfg  = STATUS_CFG[s.status] || STATUS_CFG.uploading;
              const Icon = cfg.icon;
              const isEx = expanded === s.id;
              const det  = detail[s.id];
              const t    = s.tax_summary_preview;

              return (
                <div key={s.id} className="rounded-xl overflow-hidden"
                     style={{ border: '1px solid var(--border)', backgroundColor: 'var(--background)' }}>
                  <button className="w-full p-4 flex items-center gap-3 text-left hover:bg-muted/30 transition-colors"
                          onClick={() => toggleExpand(s.id)}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                         style={{ backgroundColor: cfg.color + '20' }}>
                      <Icon size={15} style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">FY {s.financial_year}</span>
                        {s.itr_category && <span className="text-xs opacity-50">{s.itr_category}</span>}
                        <span className="text-xs px-1.5 py-0.5 rounded-full"
                              style={{ backgroundColor: cfg.color + '20', color: cfg.color, fontSize: '10px' }}>
                          {cfg.label}
                        </span>
                        {s.regime_selected && (
                          <span className="text-xs opacity-40">{s.regime_selected.toUpperCase()}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs opacity-40 font-mono">{s.user_id.slice(0,12)}…</span>
                        {t && (
                          <span className="text-xs opacity-50">
                            ₹{(t.gross_income||0).toLocaleString('en-IN')} income ·
                            ₹{(t.tax_payable||0).toLocaleString('en-IN')} tax
                          </span>
                        )}
                        <span className="text-xs opacity-30">
                          {new Date(s.created_at).toLocaleDateString('en-IN')}
                        </span>
                      </div>
                    </div>
                    {isEx ? <ChevronUp size={14} className="opacity-40" /> : <ChevronDown size={14} className="opacity-40" />}
                  </button>

                  {isEx && (
                    <div className="border-t px-4 pb-4 pt-3 space-y-4"
                         style={{ borderColor: 'var(--border)' }}>
                      {!det
                        ? <p className="text-sm opacity-40 text-center py-3">Loading…</p>
                        : (
                          <>
                            {/* Tax summary grid */}
                            {det.tax_summary && (
                              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                                {[
                                  ['Gross Income',  fmt(det.tax_summary.gross_income)],
                                  ['Deductions',    fmt(det.tax_summary.total_deductions)],
                                  ['Old Regime',    fmt(det.tax_summary.tax_old_regime)],
                                  ['New Regime',    fmt(det.tax_summary.tax_new_regime)],
                                  ['TDS Deducted',  fmt(det.tax_summary.tds_total)],
                                  ['Refund / Due',  fmt(det.tax_summary.tax_refund_or_due)],
                                ].map(([k, v]) => (
                                  <div key={k} className="rounded-lg p-2 text-center"
                                       style={{ backgroundColor: 'var(--muted)' }}>
                                    <p className="text-xs opacity-50">{k}</p>
                                    <p className="text-sm font-semibold mt-0.5">{v}</p>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Documents */}
                            <div>
                              <p className="text-xs font-medium opacity-60 mb-2">Documents ({det.documents?.length || 0})</p>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {(det.documents || []).map(d => (
                                  <div key={d.id} className="flex items-center gap-2 text-xs rounded-lg p-2"
                                       style={{ backgroundColor: 'var(--muted)' }}>
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                      d.validation_status === 'valid' ? 'bg-green-500' :
                                      d.validation_status === 'invalid' ? 'bg-red-500' : 'bg-yellow-400'
                                    }`} />
                                    <span className="opacity-70 truncate">{d.document_type}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Processing log */}
                            {det.logs?.length > 0 && (
                              <div>
                                <p className="text-xs font-medium opacity-60 mb-2">Processing log</p>
                                <div className="space-y-1 font-mono text-xs opacity-60 max-h-40 overflow-y-auto">
                                  {det.logs.map((l, i) => (
                                    <div key={i} className="flex gap-2">
                                      <span className="opacity-40">{l.created_at.slice(11,19)}</span>
                                      <span className={`${l.status === 'failed' ? 'text-red-500' : l.status === 'completed' ? 'text-green-600' : ''}`}>
                                        [{l.step}] {l.status}
                                      </span>
                                      {l.message && <span className="opacity-60 truncate">{l.message}</span>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="flex items-center justify-center gap-3">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <span className="text-sm opacity-60">Page {page} of {Math.ceil(total / 20)}</span>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}